import React, { useRef, useState } from 'react';
import { Plus, Trash2, ImagePlus, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { EditorShell, inputCls, labelCls } from './EditorShell';
import { useContent } from '../../context/ContentContext';
import { formatApiError, pictureUrl } from '../../lib/api';

const PersonRow = ({ p, i, onChange, onRemove, onMove, roles }) => {
  const { uploadImage } = useContent();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { onChange({ ...p, image: await uploadImage(file) }); toast.success('Biletet er lasta opp'); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="rounded-2xl border border-white/10 p-4 space-y-3" data-testid={`person-row-${i}`}>
      <div className="flex items-start gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`person-image-button-${i}`} className="relative w-24 h-28 rounded-xl overflow-hidden bg-[#1a1424] border border-dashed border-white/20 shrink-0 flex items-center justify-center text-white/40 hover:border-[#ff2e2e] hover:text-[#ff2e2e]">
          {p.image ? <img src={pictureUrl(p.image)} alt="" className="absolute inset-0 w-full h-full object-cover object-top" /> : null}
          <span className="relative bg-black/60 rounded-full p-2">{busy ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <div><label className={labelCls}>Namn</label><input className={inputCls} value={p.name} onChange={(e) => onChange({ ...p, name: e.target.value })} data-testid={`person-name-${i}`} /></div>
          <div><label className={labelCls}>Rolle</label><input className={inputCls} list={`roles-${i}`} value={p.role} onChange={(e) => onChange({ ...p, role: e.target.value })} /><datalist id={`roles-${i}`}>{roles.map((r) => <option key={r} value={r} />)}</datalist></div>
          <div className="sm:col-span-2"><label className={labelCls}>Instagram-lenke</label><input className={inputCls} placeholder="https://www.instagram.com/…" value={p.instagram || ''} onChange={(e) => onChange({ ...p, instagram: e.target.value })} /></div>
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={!!p.comingSoon} onChange={(e) => onChange({ ...p, comingSoon: e.target.checked })} className="accent-[#ff2e2e] w-4 h-4" /> Vis som «Kommer snart» (spørsmålsteikn i staden for bilete)
          </label>
        </div>
        <div className="flex flex-col gap-1">
          <button type="button" onClick={() => onMove(-1)} aria-label="Flytt opp" className="w-9 h-9 rounded-xl border border-white/10 text-white/50 hover:text-white flex items-center justify-center"><ChevronUp size={16} /></button>
          <button type="button" onClick={() => onMove(1)} aria-label="Flytt ned" className="w-9 h-9 rounded-xl border border-white/10 text-white/50 hover:text-white flex items-center justify-center"><ChevronDown size={16} /></button>
          <button type="button" onClick={onRemove} aria-label="Fjern person" data-testid={`person-remove-${i}`} className="w-9 h-9 rounded-xl border border-white/10 text-white/50 hover:text-red-400 hover:border-red-500 flex items-center justify-center"><Trash2 size={16} /></button>
        </div>
      </div>
      <div><label className={labelCls}>Om personen (tom linje = nytt avsnitt)</label><textarea className={`${inputCls} resize-y`} rows={3} value={(p.bio || []).join('\n\n')} onChange={(e) => onChange({ ...p, bio: e.target.value.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean) })} /></div>
    </div>
  );
};

export const PeopleEditor = ({ contentKey, title, initial, roles, onClose }) => {
  const { saveContent } = useContent();
  const [list, setList] = useState(initial);
  const [saving, setSaving] = useState(false);

  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
  };

  const save = async () => {
    setSaving(true);
    try { await saveContent(contentKey, list.filter((p) => p.name)); toast.success('Lagra!'); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <EditorShell title={title} onClose={onClose} onSave={save} saving={saving} testId={`${contentKey}-editor`}>
      {list.map((p, i) => (
        <PersonRow key={p.id} p={p} i={i} roles={roles} onChange={(np) => setList(list.map((x, j) => (j === i ? np : x)))} onRemove={() => setList(list.filter((_, j) => j !== i))} onMove={(d) => move(i, d)} />
      ))}
      <button type="button" data-testid="people-add-button" onClick={() => setList([...list, { id: `p${Date.now()}`, name: '', role: roles[0], image: '', bio: [], instagram: '', comingSoon: false }])} className="w-full py-3 rounded-2xl border border-dashed border-white/20 text-white/60 font-semibold hover:border-[#ff2e2e] hover:text-[#ff2e2e] inline-flex items-center justify-center gap-2"><Plus size={16} /> Legg til person</button>
    </EditorShell>
  );
};
